// src/core/collaborator/CollaboratorContext.tsx
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { KioskoApiErrorShape, KioskoCollaboratorInfo } from "../../services/kiosko";
import {
  setEncryptedCollaborator,
  getEncryptedCollaborator,
  clearEncryptedCollaborator,
} from "../../services/collaboratorStore";
import { useToast } from "../../hook/useToast";
import { useAuth } from "../auth";
import { fetchCollaboratorInfo } from "../../services/talentoZeus/endpoints";
import { beginGlobalLoading, endGlobalLoading } from "../../features/ui/uiSlice";
import { store } from "../../app/store";

type State = {
  collaborator: KioskoCollaboratorInfo | null;
  loading: boolean;
  lastQuery: string | null;
  error: string | null;
};

type Actions = {
  searchById: (
    identificadorApp: string,
    opts?: { silentToast?: boolean }
  ) => Promise<KioskoCollaboratorInfo | null>;
  clear: () => void;
  restoreFromStorage: () => Promise<void>;
};

type Ctx = State & Actions;

const C = createContext<Ctx>(null as any);

export function useCollaborator() {
  return useContext(C as React.Context<State & Actions>);
}

export function CollaboratorProvider({ children }: { children: React.ReactNode }) {
  const [collaborator, setCollaborator] = useState<KioskoCollaboratorInfo | null>(null);
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { user } = useAuth();

  const clear = useCallback(() => {
    setCollaborator(null);
    setLastQuery(null);
    setError(null);
    clearEncryptedCollaborator();
  }, []);

  const restoreFromStorage = useCallback(async () => {
    const saved = await getEncryptedCollaborator<KioskoCollaboratorInfo | null>();
    setCollaborator(saved ?? null);
  }, []);

  const mapStatusToMsg = (status?: number, backend?: KioskoApiErrorShape): string => {
    if (status === 401) return "No autorizado";
    if (status === 403) return "Prohibido";
    if (status === 404) return "No encontrado";
    if (status === 429) return "Demasiadas solicitudes";
    if (status && status >= 500) return "Error del servidor";
    const backendMsg =
      (backend as any)?.error?.message ||
      (Array.isArray((backend as any)?.details)
        ? (backend as any)?.details.join(" ")
        : (backend as any)?.details) ||
      (backend as any)?.message;
    return backendMsg || "Error desconocido";
  };

  const searchById = useCallback(
    async (identificadorApp: string, opts?: { silentToast?: boolean }) => {
      setLastQuery(identificadorApp);
      try {
        store.dispatch(beginGlobalLoading());
        const idEmpresa = user?.companyId ?? "";
        const info = await fetchCollaboratorInfo({
          identificador_app: identificadorApp,
          id_empresa: idEmpresa,
        });

        setCollaborator(info?.collaboratorInfo);

        await setEncryptedCollaborator(info?.collaboratorInfo);
        if (!opts?.silentToast) toast.success("Colaborador encontrado");
        return info?.collaboratorInfo;
      } catch (e: any) {
        const status = e?.response?.status as number | undefined;
        const backend = e?.response?.data as KioskoApiErrorShape | undefined;
        const msg = mapStatusToMsg(status, backend);

        if (!opts?.silentToast)
          toast.error("Hay algún problema al buscar ese ID", e.message || msg);
        throw new Error(msg);
      } finally {
        store.dispatch(endGlobalLoading());
      }
    },
    [toast, user?.companyId]
  );

  const value = useMemo(
    () => ({
      collaborator,
      lastQuery,
      error,
      searchById,
      clear,
      restoreFromStorage,
    }),
    [collaborator, lastQuery, error, searchById, clear, restoreFromStorage]
  );

  return <C.Provider value={value as State & Actions}>{children}</C.Provider>;
}
